import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { batch } from 'react-redux';

import {
  CLASS_TEMPLATE_BULK_UPDATE_TYPES,
  CLASS_TYPES,
  parseAcademyTopicClassType,
  setBulkUpdateSyncConfirmModalState,
  setBulkUpdateSyncType,
  useGetAcademyTopicQuery,
} from 'features/classCreation';
import BulkUpdateSyncButton from './BulkUpdateSyncButton';
import BulkUpdateSyncConfirmModal from './BulkUpdateSyncConfirmModal';
import EditableSection from 'components/EditableSection';
import EditAcademyTopicForm from './EditAcademyTopicForm';
import EditContestForm from './EditContestForm';
import EditLectureForm from './EditLectureForm';
import EditNonLiveLectureForm from './EditNonLiveLectureForm';
import EditTaSkillForm from './EditTaSkillForm';
import ErrorLayout from 'components/ErrorLayout';
import GlobalLoader from 'components/GlobalLoader';
import Loader from 'components/Loader';
import useAppDispatch from 'hooks/useAppDispatch';

const EDIT_CLASS_FORM_MAP = {
  [CLASS_TYPES.lecture]: EditLectureForm,
  [CLASS_TYPES.contest]: EditContestForm,
  [CLASS_TYPES.classWithoutLiveLecture]: EditNonLiveLectureForm,
};

export default function EditClassPage() {
  const dispatch = useAppDispatch();
  const { academyTopicId } = useParams();
  const { isLoading, isFetching, error, data, refetch } =
    useGetAcademyTopicQuery(Number(academyTopicId));

  const handleBulkUpdateSyncing = useCallback(
    (bulkUpdateSyncType) => {
      batch(() => {
        dispatch(setBulkUpdateSyncType(bulkUpdateSyncType));
        dispatch(setBulkUpdateSyncConfirmModalState(true));
      });
    },
    [dispatch]
  );

  if (isLoading) {
    return <Loader />;
  } else if (error) {
    return (
      <ErrorLayout
        title="Failed to load Class Template"
        message={error.data?.message || 'Something went wrong! Try again later'}
        actionFn={refetch}
      />
    );
  } else if (data?.data) {
    const academyTopic = data?.data;
    const classTag = data?.meta?.['class_tag'];

    const classType = parseAcademyTopicClassType(academyTopic);
    const EditClassFormComponent = EDIT_CLASS_FORM_MAP[classType];

    return (
      <>
        <GlobalLoader isVisible={isFetching} />
        <BulkUpdateSyncConfirmModal academyTopic={academyTopic} />
        <EditableSection
          canEdit
          title="Lecture Activity"
          titleClassName="dark bold"
          className="m-b-s"
          actions={
            <BulkUpdateSyncButton
              handleOnSync={() =>
                handleBulkUpdateSyncing(
                  CLASS_TEMPLATE_BULK_UPDATE_TYPES.academyTopics
                )
              }
            />
          }
        >
          {(editableProps) => (
            <EditAcademyTopicForm
              {...editableProps}
              academyTopic={academyTopic}
            />
          )}
        </EditableSection>
        <EditableSection
          canEdit
          title="TA Skills"
          titleClassName="dark bold"
          className="m-b-s"
          actions={
            <BulkUpdateSyncButton
              handleOnSync={() =>
                handleBulkUpdateSyncing(
                  CLASS_TEMPLATE_BULK_UPDATE_TYPES.courseGroups
                )
              }
            />
          }
        >
          {(editableProps) => (
            <EditTaSkillForm
              {...editableProps}
              academyTopic={academyTopic}
            />
          )}
        </EditableSection>
        {EditClassFormComponent && (
          <EditClassFormComponent
            academyTopic={academyTopic}
            classTag={classTag}
          />
        )}
      </>
    );
  }
}
